/**
 * Production-safe logging utility
 * Only logs errors in production, provides full logging in development
 */

/* eslint-disable no-console */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  context?: string;
  data?: unknown;
  timestamp: Date;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private logQueue: LogEntry[] = [];
  private maxQueueSize = 100;

  /**
   * Debug logging - only in development
   */
  debug(message: string, context?: string, data?: unknown): void {
    if (this.isDevelopment) {
      this.log('debug', message, context, data);
      console.debug(this.formatMessage('DEBUG', message, context), data || '');
    }
  }

  /**
   * Info logging - only in development
   */
  info(message: string, context?: string, data?: unknown): void {
    if (this.isDevelopment) {
      this.log('info', message, context, data);
      console.info(this.formatMessage('INFO', message, context), data || '');
    }
  }

  /**
   * Warning logging - reduced in production
   */
  warn(message: string, context?: string, data?: unknown): void {
    this.log('warn', message, context, data);

    if (this.isDevelopment) {
      console.warn(this.formatMessage('WARN', message, context), data || '');
    } else {
      // In production, only log critical warnings to avoid console noise
      if (this.isCriticalWarning(message, context)) {
        console.warn(this.formatMessage('WARN', message, context));
      }
    }
  }

  /**
   * Error logging - always logged
   */
  error(message: string, context?: string, data?: unknown): void {
    this.log('error', message, context, data);
    console.error(this.formatMessage('ERROR', message, context), data || '');
  }

  /**
   * Log API errors with structured data
   */
  apiError(endpoint: string, error: Error, requestData?: unknown): void {
    this.error(`API request failed: ${error.message}`, `API:${endpoint}`, {
      error: error.name,
      requestData,
    });
  }

  /**
   * Log service errors with context
   */
  serviceError(
    service: string,
    operation: string,
    error: Error,
    metadata?: unknown
  ): void {
    this.error(
      `Service operation failed: ${operation} - ${error.message}`,
      `${service}Service`,
      { error: error.name, operation, metadata }
    );
  }

  /**
   * Get recent logs for debugging
   */
  getRecentLogs(count = 50): LogEntry[] {
    return this.logQueue.slice(-count);
  }

  /**
   * Clear log queue
   */
  clearLogs(): void {
    this.logQueue = [];
  }

  private log(
    level: LogLevel,
    message: string,
    context?: string,
    data?: unknown
  ): void {
    const entry: LogEntry = {
      level,
      message,
      context,
      data,
      timestamp: new Date(),
    };

    this.logQueue.push(entry);

    // Keep queue size manageable
    if (this.logQueue.length > this.maxQueueSize) {
      this.logQueue = this.logQueue.slice(-this.maxQueueSize);
    }
  }

  private formatMessage(
    level: string,
    message: string,
    context?: string
  ): string {
    const timestamp = new Date().toISOString().slice(11, 23); // HH:mm:ss.SSS
    const contextStr = context ? ` [${context}]` : '';
    return `${timestamp} ${level}${contextStr}: ${message}`;
  }

  private isCriticalWarning(message: string, context?: string): boolean {
    const criticalPatterns = [
      /registration failed/i,
      /update failed/i,
      /service worker/i,
      /critical/i,
      /security/i,
      /auth/i,
    ];

    const criticalContexts = ['PWA', 'Auth', 'Security', 'API'];

    return (
      criticalPatterns.some((pattern) => pattern.test(message)) ||
      Boolean(context && criticalContexts.some((ctx) => context.includes(ctx)))
    );
  }
}

// Export singleton instance
export const logger = new Logger();

// Export named functions for common use cases
export const logDebug = logger.debug.bind(logger);
export const logWarn = logger.warn.bind(logger);
export const logError = logger.error.bind(logger);
export const logApiError = logger.apiError.bind(logger);

/**
 * Development-only warning - completely silent in production
 * Use for non-critical warnings that are only useful during development
 */
export const devWarn = (message: string, ...args: unknown[]): void => {
  if (import.meta.env.DEV) {
    console.warn(message, ...args);
  }
};

/**
 * Development-only error - completely silent in production
 * Use for non-critical errors that are only useful during development
 */
export const devError = (message: string, ...args: unknown[]): void => {
  if (import.meta.env.DEV) {
    console.error(message, ...args);
  }
};
